import { Injectable } from "@angular/core";
import { SelectOption } from "@shared/models/select-option.interface";
import { createCustomerLogoPath } from "@shared/utils/customer-logo-path.util";
import { Observable } from "rxjs";
import { map, shareReplay, take, tap } from "rxjs/operators";
import { CustomerService } from "@shared/services/customer/customer.service";
import { Customer } from "src/app/shared/models/customer.interface";

@Injectable({
	providedIn: "root"
})
export class CustomerCacheService {
	allCustomersCache: Customer[] = [];
	private allCustomersRequest$?: Observable<Customer[]>;

	constructor(private customerService: CustomerService) {}

	getAllCustomers(): Observable<Customer[]> {
		// Make it a little faster by caching the customers, with the tradeoff of potentially stale data.
		// (users must refresh if a new customer is added)
		return this.getFromCacheFallBackToApi().pipe(take(1));
	}

	getCustomerById(id: string): Observable<Customer> {
		return this.getAllCustomers().pipe(
			take(1),
			map(customers => customers.find(customer => customer.id === id) ?? ({} as Customer))
		);
	}

	refreshCache(): void {
		this.allCustomersCache = [];
		this.allCustomersRequest$ = undefined;
		this.getFromCacheFallBackToApi(true).pipe(take(1)).subscribe();
	}

	getCustomersMapper(): Observable<SelectOption[]> {
		return this.getAllCustomers().pipe(
			map((customers: Customer[]) => {
				return customers.flatMap(customer => {
					const option: SelectOption = {
						value: customer.id,
						text: `${customer.externalId} - ${customer.name}`,
						imagePath: createCustomerLogoPath(customer).imagePath,
						placeholder: customer.name,
						externalId: customer.externalId ? customer.externalId : ""
					};

					return option;
				});
			})
		);
	}

	private getFromCacheFallBackToApi(refresh = false): Observable<Customer[]> {
		if (!refresh && this.allCustomersCache.length > 0) {
			return new Observable(observer => observer.next(this.allCustomersCache));
		}

		if (!this.allCustomersRequest$ || refresh) {
			this.allCustomersRequest$ = this.customerService.getCustomers(false, true).pipe(
				tap(customers => (this.allCustomersCache = customers)),
				shareReplay({ bufferSize: 1, refCount: false })
			);
		}

		return this.allCustomersRequest$;
	}
}
